import { Alert, Button, Card, Divider, Group, ScrollArea, Skeleton, Stack, Text, Timeline, Title } from "@mantine/core";
import {
  IconAlertTriangle, IconBuildingBank, IconCoins, IconFileText, IconHistory,
  IconReportMoney, IconRepeat, IconUsers
} from "@tabler/icons-react";
import type { ActivityItem } from "../../apis/tenantActivity";
import { formatDate, formatRelativeTime } from "../../utils/format";
import { EmptyState } from "./EmptyState";

interface Props {
  items: ActivityItem[];
  loading: boolean;
  error?: unknown;
  onRetry?: () => void;
  title?: string;
  emptyText?: string;
}

// Maps an audit action to a Tabler icon + semantic timeline color.
function visual(type: string): { Icon: typeof IconHistory; color: string } {
  const t = (type ?? "").toUpperCase();
  let color = "gray";
  if (/TERMINATED|CANCELLED|REVOKED|FAILED|WAIVED/.test(t)) color = "red";
  else if (/SUSPENDED|DELINQUENCY/.test(t)) color = "orange";
  else if (/ACTIVATED|RESUMED|PAID|COMPLETED|CREATED/.test(t)) color = "green";
  else if (/SCHEDULED/.test(t)) color = "cyan";
  else if (/UPDATED|SAVED|SUPERSEDED/.test(t)) color = "blue";

  let Icon = IconHistory;
  if (t.startsWith("CONTRACT")) Icon = IconFileText;
  else if (t.startsWith("POLICY")) Icon = IconReportMoney;
  else if (t.startsWith("ASSIGNMENT")) Icon = IconUsers;
  else if (t.startsWith("ACCOUNT")) Icon = IconBuildingBank;
  else if (t.startsWith("CHARGE")) Icon = IconCoins;
  else if (t.startsWith("DELINQUENCY")) Icon = IconAlertTriangle;
  else if (t.startsWith("EC_")) Icon = IconRepeat;
  return { Icon, color };
}

// Shared activity timeline used by the platform-admin Overview tab (UI-SPEC 2.2)
// and the tenant dashboard (UI-SPEC 3.1) so the two never drift.
export function ActivityFeed({
  items,
  loading,
  error,
  onRetry,
  title = "Идэвхжил",
  emptyText = "Идэвхжил алга байна."
}: Props) {
  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="sm">
        <Title order={5}>{title}</Title>
        <Divider />

        {loading && (
          <Stack gap="md" pt={4}>
            {[0, 1, 2].map((i) => (
              <Group key={i} gap="sm" wrap="nowrap">
                <Skeleton height={18} circle width={18} />
                <Skeleton height={12} width={`${70 - i * 12}%`} />
              </Group>
            ))}
          </Stack>
        )}

        {!loading && Boolean(error) && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Идэвхжил ачаалж чадсангүй">
            <Stack gap="xs" align="flex-start">
              <Text size="sm">Дахин оролдоно уу.</Text>
              {onRetry && (
                <Button size="xs" variant="light" color="red" onClick={onRetry}>
                  Дахин ачаалах
                </Button>
              )}
            </Stack>
          </Alert>
        )}

        {!loading && !error && items.length === 0 && (
          <EmptyState icon={<IconHistory size={26} />} message={emptyText} />
        )}

        {!loading && !error && items.length > 0 && (
          <ScrollArea.Autosize mah={360} type="hover" offsetScrollbars>
            <Timeline active={items.length} bulletSize={26} lineWidth={2} pr="sm">
              {items.map((it) => {
                const { Icon, color } = visual(it.type);
                return (
                  <Timeline.Item
                    key={it.id}
                    color={color}
                    bullet={<Icon size={14} />}
                    title={<Text size="sm" fw={500}>{it.summary}</Text>}
                  >
                    <Group gap={6} wrap="nowrap">
                      <Text size="xs" c="dimmed">{formatRelativeTime(it.occurredAt)}</Text>
                      <Text size="xs" c="dimmed">·</Text>
                      <Text size="xs" c="dimmed">{formatDate(it.occurredAt)}</Text>
                    </Group>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </ScrollArea.Autosize>
        )}
      </Stack>
    </Card>
  );
}
